<div class="container my-3">
    <div class="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
        <h1 class="mb-0">Quote Reports</h1>
        <span class="badge bg-secondary">{{ scopeLabel() }}</span>
    </div>

    <!-- Filters -->
    <div class="card my-3">
        <div class="card-body">
            <div class="row g-3 align-items-end">
                <div class="col-12 col-md-4">
                    <label for="report-preset" class="form-label">Range</label>
                    <select id="report-preset" class="form-select" [(ngModel)]="preset" (ngModelChange)="applyPreset()">
                        @for (option of presets; track option.id) {
                        <option [value]="option.id">{{ option.label }}</option>
                        }
                    </select>
                </div>
                <div class="col-6 col-md-4">
                    <label for="report-from" class="form-label">From</label>
                    <input id="report-from" type="date" class="form-control" [max]="to" [(ngModel)]="from"
                        (change)="onDateChange()" />
                </div>
                <div class="col-6 col-md-4">
                    <label for="report-to" class="form-label">To</label>
                    <input id="report-to" type="date" class="form-control" [min]="from" [(ngModel)]="to"
                        (change)="onDateChange()" />
                </div>
            </div>

            <div class="mt-3 d-flex flex-wrap align-items-center gap-2">
                <span class="small text-muted me-1">Status</span>
                @for (status of allStatuses; track status) {
                <button type="button" class="btn btn-sm text-capitalize"
                    [ngClass]="isStatusSelected(status) ? 'btn-primary' : 'btn-outline-primary'"
                    [attr.aria-pressed]="isStatusSelected(status)" (click)="toggleStatus(status)">{{ status }}</button>
                }
                @if (selectedStatuses.length === 0) {
                <span class="small text-muted">All statuses</span>
                }
            </div>
        </div>
    </div>

    @if (error) {
    <div class="alert alert-danger">{{ error }}</div>
    }

    @if (loading && !report) {
    <app-loading></app-loading>
    }

    @if (report) {
    <div [class.report-stale]="loading">
        @let summary = report.summary;

        @if (summary.unpricedQuoteCount > 0) {
        <div class="alert alert-warning">
            {{ summary.unpricedQuoteCount }} quote(s) could not be priced and are left out of the amounts below.
        </div>
        }

        <!-- Summary -->
        <div class="row g-3 mb-3">
            <div class="col-6 col-lg-3">
                <div class="card h-100">
                    <div class="card-body">
                        <div class="stat-label">Quotes</div>
                        <div class="stat-value">{{ summary.quoteCount | number }}</div>
                    </div>
                </div>
            </div>
            <div class="col-6 col-lg-3">
                <div class="card h-100">
                    <div class="card-body">
                        <div class="stat-label">Total</div>
                        <div class="stat-value">{{ summary.totalAmount | currency }}</div>
                    </div>
                </div>
            </div>
            <div class="col-6 col-lg-3">
                <div class="card h-100">
                    <div class="card-body">
                        <div class="stat-label">Subtotal</div>
                        <div class="stat-value">{{ summary.subTotalAmount | currency }}</div>
                        <div class="small text-muted">Before discount, shipping, tariffs and tax</div>
                    </div>
                </div>
            </div>
            <div class="col-6 col-lg-3">
                <div class="card h-100">
                    <div class="card-body">
                        <div class="stat-label">Average Quote</div>
                        <div class="stat-value">{{ summary.averageTotalAmount | currency }}</div>
                    </div>
                </div>
            </div>
        </div>

        @if (summary.statuses.length > 0) {
        <div class="card mb-4">
            <div class="card-body d-flex flex-wrap gap-4">
                @for (status of summary.statuses; track status.status) {
                <div>
                    <span class="badge text-capitalize" [ngClass]="statusClass(status.status)">{{ status.status }}</span>
                    <div class="fw-semibold mt-1">{{ status.quoteCount | number }} quotes</div>
                    <div class="small text-muted">{{ status.totalAmount | currency }}</div>
                </div>
                }
            </div>
        </div>
        }

        <!-- Creators -->
        <h4>Quotes by Creator</h4>
        @if (report.creators.length > 0) {
        <div class="table-responsive mb-4">
            <table class="table table-sm table-hover align-middle">
                <thead>
                    <tr>
                        <th [attr.aria-sort]="ariaSort(creatorSort, 'creatorName')">
                            <button type="button" class="sort-button" (click)="sortCreatorsBy('creatorName')">
                                Creator <span class="sort-indicator">{{ sortIndicator(creatorSort, 'creatorName') }}</span>
                            </button>
                        </th>
                        <th class="text-end" [attr.aria-sort]="ariaSort(creatorSort, 'quoteCount')">
                            <button type="button" class="sort-button" (click)="sortCreatorsBy('quoteCount')">
                                Quotes <span class="sort-indicator">{{ sortIndicator(creatorSort, 'quoteCount') }}</span>
                            </button>
                        </th>
                        @for (status of allStatuses; track status) {
                        <th class="text-end d-none d-md-table-cell" [attr.aria-sort]="ariaSort(creatorSort, status)">
                            <button type="button" class="sort-button text-capitalize" (click)="sortCreatorsBy(status)">
                                {{ status }} <span class="sort-indicator">{{ sortIndicator(creatorSort, status) }}</span>
                            </button>
                        </th>
                        }
                        <th class="text-end" [attr.aria-sort]="ariaSort(creatorSort, 'subTotalAmount')">
                            <button type="button" class="sort-button" (click)="sortCreatorsBy('subTotalAmount')">
                                Subtotal <span class="sort-indicator">{{ sortIndicator(creatorSort, 'subTotalAmount') }}</span>
                            </button>
                        </th>
                        <th class="text-end" [attr.aria-sort]="ariaSort(creatorSort, 'totalAmount')">
                            <button type="button" class="sort-button" (click)="sortCreatorsBy('totalAmount')">
                                Total <span class="sort-indicator">{{ sortIndicator(creatorSort, 'totalAmount') }}</span>
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    @for (creator of creators; track creator.creatorId) {
                    <tr>
                        <td>{{ creator.creatorName }}</td>
                        <td class="text-end">{{ creator.quoteCount | number }}</td>
                        @for (status of allStatuses; track status) {
                        <td class="text-end d-none d-md-table-cell">{{ statusCount(creator.statuses, status) | number }}</td>
                        }
                        <td class="text-end">{{ creator.subTotalAmount | currency }}</td>
                        <td class="text-end">{{ creator.totalAmount | currency }}</td>
                    </tr>
                    }
                </tbody>
                <tfoot>
                    <tr>
                        <th>Total</th>
                        <td class="text-end">{{ creatorTotals.quoteCount | number }}</td>
                        @for (status of allStatuses; track status) {
                        <td class="text-end d-none d-md-table-cell">{{ (creatorTotals.statuses[status] ?? 0) | number }}</td>
                        }
                        <td class="text-end">{{ creatorTotals.subTotalAmount | currency }}</td>
                        <td class="text-end">{{ creatorTotals.totalAmount | currency }}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
        }
        @else {
        <p class="text-muted mb-4">No quotes were created in this range.</p>
        }

        <!-- Products -->
        <h4>Quoted Products</h4>
        @if (report.products.length > 0) {
        <div class="table-responsive mb-2">
            <table class="table table-sm table-hover align-middle">
                <thead>
                    <tr>
                        <th [attr.aria-sort]="ariaSort(productSort, 'sku')">
                            <button type="button" class="sort-button" (click)="sortProductsBy('sku')">
                                SKU <span class="sort-indicator">{{ sortIndicator(productSort, 'sku') }}</span>
                            </button>
                        </th>
                        <th [attr.aria-sort]="ariaSort(productSort, 'name')">
                            <button type="button" class="sort-button" (click)="sortProductsBy('name')">
                                Name <span class="sort-indicator">{{ sortIndicator(productSort, 'name') }}</span>
                            </button>
                        </th>
                        <th class="d-none d-md-table-cell" [attr.aria-sort]="ariaSort(productSort, 'productCategory')">
                            <button type="button" class="sort-button" (click)="sortProductsBy('productCategory')">
                                Category <span class="sort-indicator">{{ sortIndicator(productSort, 'productCategory') }}</span>
                            </button>
                        </th>
                        <th class="text-end" [attr.aria-sort]="ariaSort(productSort, 'quoteCount')">
                            <button type="button" class="sort-button" (click)="sortProductsBy('quoteCount')">
                                Quotes <span class="sort-indicator">{{ sortIndicator(productSort, 'quoteCount') }}</span>
                            </button>
                        </th>
                        <th class="text-end" [attr.aria-sort]="ariaSort(productSort, 'totalQuantity')">
                            <button type="button" class="sort-button" (click)="sortProductsBy('totalQuantity')">
                                Quantity <span class="sort-indicator">{{ sortIndicator(productSort, 'totalQuantity') }}</span>
                            </button>
                        </th>
                        <th class="text-end" [attr.aria-sort]="ariaSort(productSort, 'totalAmount')">
                            <button type="button" class="sort-button" (click)="sortProductsBy('totalAmount')">
                                Amount <span class="sort-indicator">{{ sortIndicator(productSort, 'totalAmount') }}</span>
                            </button>
                        </th>
                    </tr>
                </thead>
                <tfoot>
                    <tr>
                        <th>Total</th>
                        <td>{{ products.length | number }} products</td>
                        <td class="d-none d-md-table-cell"></td>
                        <!-- A quote can hold several products, so quote counts do not add up -->
                        <td></td>
                        <td class="text-end">{{ productTotals.totalQuantity | number }}</td>
                        <td class="text-end">{{ productTotals.totalAmount | currency }}</td>
                    </tr>
                </tfoot>
                <tbody>
                    @for (product of pagedProducts(); track product.productId) {
                    <tr>
                        <td class="text-nowrap">{{ product.sku }}</td>
                        <td>{{ product.name }}</td>
                        <td class="d-none d-md-table-cell">{{ product.productCategory }}</td>
                        <td class="text-end">{{ product.quoteCount | number }}</td>
                        <td class="text-end">{{ product.totalQuantity | number }}</td>
                        <td class="text-end">{{ product.totalAmount | currency }}</td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>
        @if (products.length > productPageSize) {
        <ngb-pagination [(page)]="productPage" [pageSize]="productPageSize" [collectionSize]="products.length"
            [maxSize]="10" [boundaryLinks]="true"></ngb-pagination>
        }
        <p class="small text-muted">Product amounts are before quote discounts, shipping, tariffs and tax, so they add
            up to about the subtotal rather than the total.</p>
        }
        @else {
        <p class="text-muted">No products were quoted in this range.</p>
        }
    </div>
    }
</div>
